import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Autocomplete, TextField } from '@mui/material';
import { ArrowLeft, Plus, Trash2, ChevronDown, ChevronUp, Save, X } from 'lucide-react';
import { useNotify } from 'react-admin';
import {
  createNcrTemplate,
  getNcrTemplateById,
  updateNcrTemplate,
  type NcrTemplate,
  type NcrTemplateProperty,
  type NcrTemplateStage,
} from '../../../services/ncrTemplateService.ts';
import { getAllUsers, type User } from '../../../services/userService.ts';

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

const NcrTemplateEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const isCreate = !id;
  const isViewMode = location.pathname.endsWith('/view');

  const [template, setTemplate] = useState<NcrTemplate>({
    name: '',
    description: '',
    isActive: true,
    stages: [],
    properties: [],
  });
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getNcrTemplateById(id);
      setTemplate(data);
    } catch {
      notify('Failed to load template', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    if (!isCreate && id) fetchTemplate();
  }, [id, isCreate, fetchTemplate]);

  useEffect(() => {
    getAllUsers()
      .then(setUsers)
      .catch(() => notify('Failed to load users', { type: 'warning' }));
  }, [notify]);

  const handleSave = async () => {
    if (isViewMode) return;
    if (!template.name.trim()) {
      notify('Template name is required', { type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await (isCreate ? createNcrTemplate(template) : updateNcrTemplate(id!, template));
      notify(isCreate ? 'Template created successfully' : 'Template updated successfully');
      navigate('/NcrTemplate');
    } catch {
      notify('Failed to save template', { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  // ── Stage helpers ──
  const addStage = () => {
    const newStage: NcrTemplateStage = {
      name: `Stage ${template.stages.length + 1}`,
      description: '',
      sortOrder: template.stages.length,
      children: [],
    };
    setTemplate((prev) => ({ ...prev, stages: [...prev.stages, newStage] }));
  };

  const updateStage = (i: number, updates: Partial<NcrTemplateStage>) =>
    setTemplate((prev) => {
      const stages = [...prev.stages];
      stages[i] = { ...stages[i], ...updates };
      return { ...prev, stages };
    });

  const deleteStage = (i: number) =>
    setTemplate((prev) => ({ ...prev, stages: prev.stages.filter((_, idx) => idx !== i) }));

  const swapStages = (a: number, b: number) =>
    setTemplate((prev) => {
      const stages = [...prev.stages];
      [stages[a], stages[b]] = [stages[b], stages[a]];
      stages.forEach((s, idx) => (s.sortOrder = idx));
      return { ...prev, stages };
    });

  const toggleStage = (i: number) =>
    setExpandedStages((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  // ── Property helpers ──
  const addProperty = () => {
    const newProp: NcrTemplateProperty = {
      name: `Property ${template.properties.length + 1}`,
      description: '',
      isRequired: false,
      sortOrder: template.properties.length,
    };
    setTemplate((prev) => ({ ...prev, properties: [...prev.properties, newProp] }));
  };

  const updateProperty = (i: number, updates: Partial<NcrTemplateProperty>) =>
    setTemplate((prev) => {
      const properties = [...prev.properties];
      properties[i] = { ...properties[i], ...updates };
      return { ...prev, properties };
    });

  const deleteProperty = (i: number) =>
    setTemplate((prev) => ({ ...prev, properties: prev.properties.filter((_, idx) => idx !== i) }));

  const swapProperties = (a: number, b: number) =>
    setTemplate((prev) => {
      const properties = [...prev.properties];
      [properties[a], properties[b]] = [properties[b], properties[a]];
      properties.forEach((p, idx) => (p.sortOrder = idx));
      return { ...prev, properties };
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <div className="text-gray-600">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* Back + Title */}
      <button
        type="button"
        onClick={() => navigate('/NcrTemplate')}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      <h2 className="text-[22px] font-extrabold text-slate-900 leading-none mb-5">
        {isViewMode ? 'View NCR Template' : isCreate ? 'Create NCR Template' : 'Edit NCR Template'}
      </h2>

      {/* Template Information */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Template Name *</label>
            <input
              type="text"
              value={template.name}
              onChange={(e) => setTemplate((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter template name"
              disabled={isViewMode}
              className={inputCls}
            />
          </div>

          <div className="flex items-center gap-3 pt-6">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={template.isActive}
                onChange={(e) => setTemplate((prev) => ({ ...prev, isActive: e.target.checked }))}
                disabled={isViewMode}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5" />
            </label>
            <span className="text-sm font-medium text-gray-700">
              {template.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              value={template.description || ''}
              onChange={(e) => setTemplate((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Enter template description"
              rows={3}
              disabled={isViewMode}
              className={inputCls}
            />
          </div>
        </div>
      </div>

      {/* Stages */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Stages</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {template.stages.length} stage{template.stages.length !== 1 ? 's' : ''} defined
            </p>
          </div>
          {!isViewMode && (
            <button
              type="button"
              onClick={addStage}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <Plus size={14} /> Add Stage
            </button>
          )}
        </div>

        <div className="space-y-3">
          {template.stages.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-sm text-gray-400">
              No stages defined. Click "Add Stage" to create one.
            </div>
          ) : (
            template.stages.map((stage, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Stage Header */}
                <div
                  className="flex items-center justify-between px-5 py-3 bg-slate-50 cursor-pointer select-none hover:bg-slate-100 transition-colors"
                  onClick={() => toggleStage(i)}
                >
                  <div className="flex items-center gap-3">
                    {expandedStages.has(i) ? (
                      <ChevronUp size={18} className="text-slate-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown size={18} className="text-slate-500 flex-shrink-0" />
                    )}
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">
                      Stage {i + 1}
                    </span>
                    <span className="font-semibold text-slate-800 text-sm">{stage.name}</span>
                  </div>
                  {!isViewMode && (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="p-1 rounded hover:bg-slate-200 transition-colors disabled:opacity-30"
                        onClick={() => swapStages(i, i - 1)}
                        disabled={i === 0}
                        title="Move up"
                      >
                        <ChevronUp size={15} />
                      </button>
                      <button
                        className="p-1 rounded hover:bg-slate-200 transition-colors disabled:opacity-30"
                        onClick={() => swapStages(i, i + 1)}
                        disabled={i === template.stages.length - 1}
                        title="Move down"
                      >
                        <ChevronDown size={15} />
                      </button>
                      <button
                        className="p-1 rounded hover:bg-red-50 text-red-500 transition-colors"
                        onClick={() => deleteStage(i)}
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Stage Body */}
                {expandedStages.has(i) && (
                  <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Stage Name *</label>
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => updateStage(i, { name: e.target.value })}
                        disabled={isViewMode}
                        className={inputCls}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Owner (Optional)</label>
                      <Autocomplete
                        options={users}
                        value={users.find((u) => u.id === stage.ownerId) || null}
                        onChange={(_, value) => updateStage(i, { ownerId: value?.id })}
                        getOptionLabel={(option) => option.userName || option.email || String(option.id)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText="No users found"
                        disabled={isViewMode}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Search and select owner" size="small" />
                        )}
                        sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: '0.5rem' } }}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                      <textarea
                        value={stage.description || ''}
                        onChange={(e) => updateStage(i, { description: e.target.value })}
                        placeholder="Enter stage description"
                        rows={2}
                        disabled={isViewMode}
                        className={inputCls}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Properties */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Properties</h3>
            <p className="text-sm text-slate-500 mt-0.5">Attachment fields available on NCR instances</p>
          </div>
          {!isViewMode && (
            <button
              type="button"
              onClick={addProperty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <Plus size={14} /> Add Property
            </button>
          )}
        </div>

        {template.properties.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-sm text-gray-400">
            No properties defined. Click "Add Property" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                  <th className="py-2 pr-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Required</th>
                  {!isViewMode && (
                    <th className="py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {template.properties.map((prop, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2 pr-4">
                      <input
                        type="text"
                        value={prop.name}
                        onChange={(e) => updateProperty(i, { name: e.target.value })}
                        disabled={isViewMode}
                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="text"
                        value={prop.description || ''}
                        onChange={(e) => updateProperty(i, { description: e.target.value })}
                        disabled={isViewMode}
                        placeholder="Optional description"
                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </td>
                    <td className="py-2 pr-4 text-center">
                      <input
                        type="checkbox"
                        checked={prop.isRequired}
                        onChange={(e) => updateProperty(i, { isRequired: e.target.checked })}
                        disabled={isViewMode}
                        className="accent-blue-600 w-4 h-4 cursor-pointer"
                      />
                    </td>
                    {!isViewMode && (
                      <td className="py-2 text-right">
                        <div className="inline-flex gap-1 items-center">
                          <button
                            className="p-1 rounded hover:bg-slate-100 transition-colors disabled:opacity-30"
                            onClick={() => swapProperties(i, i - 1)}
                            disabled={i === 0}
                            title="Move up"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            className="p-1 rounded hover:bg-slate-100 transition-colors disabled:opacity-30"
                            onClick={() => swapProperties(i, i + 1)}
                            disabled={i === template.properties.length - 1}
                            title="Move down"
                          >
                            <ChevronDown size={14} />
                          </button>
                          <button
                            className="p-1 rounded hover:bg-red-50 text-red-500 transition-colors"
                            onClick={() => deleteProperty(i)}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <button
          type="button"
          onClick={() => navigate('/NcrTemplate')}
          disabled={saving}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 border border-gray-300 text-slate-700 rounded-[10px] hover:bg-gray-50 transition-colors disabled:opacity-50 font-semibold"
        >
          <X size={18} /> {isViewMode ? 'Back to List' : 'Cancel'}
        </button>
        {!isViewMode && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors disabled:opacity-50 font-semibold"
          >
            <Save size={18} /> {saving ? 'Saving…' : isCreate ? 'Create Template' : 'Save Changes'}
          </button>
        )}
      </div>
    </div>
  );
};

export default NcrTemplateEdit;
